/**
 * What the API client is allowed to say about a failed request.
 *
 * The interceptor used to log `error.response?.data || error.message` — the whole
 * response body. On this portal a failure can carry a borrower record, bank
 * details or a Confirmation-of-Payee result, and `lib/posthog.js` sets
 * `enable_recording_console_log: true`, so console output travels to PostHog with
 * the session replay and is retained there. The `SENSITIVE` regex beside that
 * setting is a `maskInputFn`: it masks input *elements* and never sees console
 * arguments.
 *
 * The status, method and path answer "what broke" without the payload, which is
 * what a developer reading a replay actually needs. Outside production the whole
 * thing is kept, because there the console is a local window rather than a sink.
 *
 * **This is not the whole class.** `maskAllInputs: false` is set deliberately, so
 * values typed into a form are already in the replay; closing that is a
 * data-protection decision rather than a code change. This closes the one site
 * that logs a response body on every failure, which is gratuitous either way.
 */

/** GET /loans/SFA-10231 -> "GET /loans/SFA-10231", with no payload. */
function requestLabel(error) {
  const config = error?.config || {};
  const method = String(config.method || "?").toUpperCase();
  return `${method} ${config.url || "(no url)"}`;
}

/**
 * One line describing a failed request.
 *
 * @param {object} error an axios error
 * @returns {string} safe to log in any environment
 */
export function describeApiFailure(error) {
  const status = error?.response?.status;
  const label = `API Error: ${requestLabel(error)}${status ? ` -> ${status}` : ""}`;

  if (process.env.NODE_ENV === "production") {
    // Deliberately neither `response.data` nor `message`: a vendor client can put
    // the body in the message, so the fallback arm leaks by the same route.
    return status ? label : `${label} (no response)`;
  }

  const detail = error?.response?.data ?? error?.message;
  return `${label} ${typeof detail === "string" ? detail : JSON.stringify(detail)}`;
}
